import { Building2, AlertTriangle } from 'lucide-react';
import { useLocation } from 'wouter';
import { format } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ShareholderRoleCardProps {
  shareholder: {
    id: string;
    accountId: string;
    accountName: string;
    entityType: string;
    role: 'shareholder';
    shareClass: string | null;
    numberOfShares: number | null;
    ownershipPercentage: number | null;
    appointmentDate: string | null;
    ceasedDate: string | null;
    isBeneficialOwner: boolean;
    isActive: boolean;
  };
}

export default function ShareholderRoleCard({ shareholder }: ShareholderRoleCardProps) {
  const [, navigate] = useLocation();

  const isCeased = !shareholder.isActive || shareholder.ceasedDate !== null;

  return (
    <Card
      className={cn(
        'border-l-4',
        isCeased ? 'border-l-muted opacity-60' : 'border-l-green-500'
      )}
    >
      <CardContent className="p-3">
        <div className="flex justify-between items-start">
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Building2 className="h-4 w-4 text-muted-foreground" />
              <span className={cn(
                'font-medium text-sm',
                isCeased && 'line-through'
              )}>
                {shareholder.accountName}
              </span>

              {shareholder.isBeneficialOwner && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="default" className="bg-amber-500 text-white hover:bg-amber-600 text-xs">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        UBO
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Ultimate Beneficial Owner - ownership ≥ 25%</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              {shareholder.shareClass && (
                <Badge variant="secondary" className="text-xs">
                  {shareholder.shareClass.replace('_', ' ')}
                </Badge>
              )}

              {isCeased && (
                <Badge variant="secondary" className="text-xs">Ceased</Badge>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              Shareholder • {shareholder.entityType.replace('_', ' ')}
            </p>

            <div className="flex gap-4">
              {shareholder.ownershipPercentage !== null && (
                <p className="text-xs text-muted-foreground">
                  Ownership: <span className="font-medium">{Number(shareholder.ownershipPercentage).toFixed(2)}%</span>
                </p>
              )}

              {shareholder.numberOfShares !== null && (
                <p className="text-xs text-muted-foreground">
                  Shares: {Number(shareholder.numberOfShares).toLocaleString()}
                </p>
              )}
            </div>

            {shareholder.appointmentDate && (
              <p className="text-xs text-muted-foreground">
                Appointed: {format(new Date(shareholder.appointmentDate), 'dd MMM yyyy')}
              </p>
            )}

            {shareholder.ceasedDate && (
              <p className="text-xs text-muted-foreground">
                Ceased: {format(new Date(shareholder.ceasedDate), 'dd MMM yyyy')}
              </p>
            )}
          </div>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/accounts/${shareholder.accountId}`)}
          >
            View Account
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
